import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { verbForDecisionKind, type ActorDTO, type AgentLane, type DecisionDTO, type ProjectDTO, type SteerItem, type TaskDTO, type Verb } from "@roomate/core";
import { getToken, useActors, useAnswer, useBridge, useProjects, useRun, useSendNote, useTasks } from "../api.ts";
import { ChiefNote } from "./ChiefNote.tsx";
import { steerBlockedReason } from "../lib/run-outcome.ts";
import { useCurrentProject } from "../project.tsx";
import { KIND_LABEL, NEEDS_RATIONALE } from "../pages/Inbox.tsx";
import { paletteActors } from "../roster.ts";
import { Button, cx, ErrorNote, inputCls, TierChip } from "../ui.tsx";

const VERB_LABEL: Record<Verb, string> = { approve: "Approve", advise: "Advise", act: "Act", steer: "Steer" };

type Entry =
  | { key: string; kind: "decision"; verb: Verb; decision: DecisionDTO; text: string }
  | { key: string; kind: "run"; lane: AgentLane; text: string }
  | { key: string; kind: "steer-flag"; item: SteerItem; text: string }
  | { key: string; kind: "project"; project: ProjectDTO; text: string }
  | { key: string; kind: "switch"; project: ProjectDTO; text: string }
  | { key: string; kind: "task"; task: TaskDTO; text: string }
  | { key: string; kind: "actor"; actor: ActorDTO; text: string }
  | { key: string; kind: "page"; path: string; label: string; text: string };

/** Pages reachable by name; the Chief's audit feed is the first (chief-of-office.md §8.3). */
const PAGE_ENTRIES: Entry[] = [{ key: "page-chief", kind: "page", path: "/chief", label: "Actions by the Chief", text: "Actions by the Chief chief audit feed undo" }];

/**
 * ⌘K / Ctrl+K command palette: navigate to projects/tasks/runs, answer a decision (approve or
 * advise) and send a steer note to a running agent, all without leaving the keyboard.
 *
 * Self-contained: mount it once near the app root (inside the router, query and project providers) and
 * it manages its own open state, data and global shortcut — see App.tsx.
 */
export function CommandPalette() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (!((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k")) return;
      if (!getToken()) return;
      e.preventDefault();
      setOpen((o) => !o);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  if (!open) return null;
  return <PaletteDialog onClose={() => setOpen(false)} />;
}

function PaletteDialog({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const bridge = useBridge();
  const projects = useProjects();
  const tasks = useTasks();
  const current = useCurrentProject();
  const actors = useActors();
  const [query, setQuery] = useState("");
  // Selection is tracked by the entry's own key, not its position: `filtered` is rebuilt on every
  // live-driven refetch (bridge/tasks/projects), so a plain array index would silently point at a
  // different row the instant the list reshuffles underneath an arrowed-to selection.
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);
  const navigatedAway = useRef(false);

  useEffect(() => {
    previouslyFocused.current = document.activeElement as HTMLElement | null;
    inputRef.current?.focus();
    return () => {
      // Skip restoring focus to the pre-palette element when we're leaving because the user
      // navigated somewhere — the destination page should receive focus, not the old trigger.
      if (!navigatedAway.current) previouslyFocused.current?.focus?.();
    };
  }, []);

  const closeExpand = () => {
    setExpanded(null);
    inputRef.current?.focus();
  };

  // Esc always works, even once Tab has moved focus into an expanded decision/steer panel: close
  // that panel first, then the whole palette on a second Esc — mirrors a native menu/dialog stack.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      e.stopPropagation();
      if (expanded) closeExpand();
      else onClose();
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [expanded, onClose]);

  const entries = useMemo<Entry[]>(() => {
    const n = bridge.data?.needsAHuman;
    const lanesById = new Map((bridge.data?.agentLanes ?? []).map((l) => [l.runId, l]));
    const decisions: Entry[] = [...(n?.approve ?? []), ...(n?.advise ?? []), ...(n?.act ?? [])].map((d) => ({
      key: `decision-${d.id}`,
      kind: "decision",
      verb: verbForDecisionKind(d.kind),
      decision: d,
      text: `${VERB_LABEL[verbForDecisionKind(d.kind)]} ${d.question}`,
    }));
    const runs: Entry[] = [...lanesById.values()].map((l) => ({
      key: `run-${l.runId}`,
      kind: "run",
      lane: l,
      text: `${l.name} #${l.taskNumber} ${l.taskTitle} run`,
    }));
    const steerFlags: Entry[] = (n?.steer ?? [])
      .filter((s) => !(s.runId && lanesById.has(s.runId)))
      .map((s, i) => ({
        key: `steer-${s.taskId}-${s.reason}-${i}`,
        kind: "steer-flag",
        item: s,
        text: `steer #${s.taskNumber} ${s.taskTitle} ${s.message}`,
      }));
    const projectEntries: Entry[] = (projects.data ?? []).map((p) => ({ key: `project-${p.id}`, kind: "project", project: p, text: p.name }));
    const switchEntries: Entry[] =
      current.projects.length > 1
        ? current.projects.filter((p) => p.id !== current.projectId).map((p) => ({ key: `switch-${p.id}`, kind: "switch", project: p, text: `Switch project ${p.name}` }))
        : [];
    const taskEntries: Entry[] = (tasks.data ?? []).map((t) => ({ key: `task-${t.id}`, kind: "task", task: t, text: `#${t.number} ${t.title}` }));
    // Reaching a profile (/team/:actorId) by name here is deliberate: the header avatar shows only
    // other people who are online (PresenceAvatars), with no self-profile affordance to hang a link
    // off, so the palette is the keyboard-first route there (docs/specs/review-delegation.md §4.8).
    const actorEntries: Entry[] = paletteActors(actors.data ?? []).map((a) => ({ key: `actor-${a.id}`, kind: "actor", actor: a, text: a.name }));
    return [...decisions, ...runs, ...steerFlags, ...switchEntries, ...PAGE_ENTRIES, ...projectEntries, ...taskEntries, ...actorEntries];
  }, [bridge.data, projects.data, tasks.data, actors.data, current.projects, current.projectId]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const cap = (kind: Entry["kind"], n: number) => entries.filter((e) => e.kind === kind && (!q || e.text.toLowerCase().includes(q))).slice(0, n);
    const all = [...cap("decision", 6), ...cap("run", 6), ...cap("steer-flag", 4), ...cap("switch", 6), ...cap("page", 2), ...cap("project", 6), ...cap("task", 8), ...cap("actor", 6)];
    // Typing a name exactly means "take me there": exact matches jump to the top, order otherwise kept.
    return q ? [...all.filter((e) => e.text.toLowerCase() === q), ...all.filter((e) => e.text.toLowerCase() !== q)] : all;
  }, [entries, query]);

  // The active row is derived synchronously from `filtered`, not reset via a useEffect: an effect
  // only runs after commit, which would leave a one-render gap — after `filtered` has already
  // updated (so the DOM shows the new results) but before an effect could fix up `activeKey` —
  // where `active` would be null and Enter would silently do nothing. Falling back to index 0
  // inline means every render is self-consistent: the highlighted row always matches what Enter
  // will do, with no gap for a fast keyboard-only interaction to land in.
  const matchedIndex = filtered.findIndex((e) => e.key === activeKey);
  const activeIndex = matchedIndex >= 0 ? matchedIndex : filtered.length ? 0 : -1;
  const active = activeIndex >= 0 ? filtered[activeIndex]! : null;

  // A fresh query always starts from the top, even if the previous selection happens to still
  // match something in the new results. (Live-driven changes that don't touch `query` are handled
  // by the derivation above, which preserves the current selection when it's still present.)
  useEffect(() => {
    setActiveKey(filtered[0]?.key ?? null);
    setExpanded(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const go = (path: string) => {
    navigatedAway.current = true;
    navigate(path);
    onClose();
  };

  const switchTo = (id: string) => {
    current.select(id);
    onClose();
  };

  const moveBy = (delta: number) => {
    if (!filtered.length) return;
    const from = activeIndex >= 0 ? activeIndex : 0;
    setActiveKey(filtered[(from + delta + filtered.length) % filtered.length]!.key);
    setExpanded(null);
  };

  const onInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      moveBy(1);
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      moveBy(-1);
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      if (!active) return;
      // Ctrl/Cmd+Enter on a run jumps straight to "steer" instead of opening the run page — the
      // one case where the palette's primary action (navigate) isn't the keyboard shortcut you want.
      if (active.kind === "run" && (e.metaKey || e.ctrlKey)) {
        setExpanded(`steer-${active.key}`);
        return;
      }
      if (active.kind === "switch") switchTo(active.project.id);
      else if (active.kind === "project") go(`/projects/${active.project.id}`);
      else if (active.kind === "task") go(`/tasks/${active.task.id}`);
      else if (active.kind === "run") go(`/runs/${active.lane.runId}`);
      else if (active.kind === "steer-flag") go(`/tasks/${active.item.taskId}`);
      else if (active.kind === "actor") go(`/team/${active.actor.id}`);
      else if (active.kind === "page") go(active.path);
      else if (active.kind === "decision") setExpanded(active.key);
    }
  };

  // A simple focus trap: Tab/Shift+Tab cycles only among the dialog's own focusable elements.
  // Disabled buttons are excluded — they can never become document.activeElement, so including
  // one as "last" would mean forward-Tab is never intercepted and focus leaks past the dialog.
  const onDialogKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== "Tab" || !dialogRef.current) return;
    const focusables = dialogRef.current.querySelectorAll<HTMLElement>('button:not([disabled]), [href], input, textarea, [tabindex]:not([tabindex="-1"])');
    if (focusables.length === 0) return;
    const first = focusables[0]!;
    const last = focusables[focusables.length - 1]!;
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-start justify-center bg-ink/30 p-4 pt-[12vh]" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
        data-testid="command-palette"
        className="w-full max-w-xl rounded-lg border border-rule bg-card shadow-2xl"
        onKeyDown={onDialogKeyDown}
      >
        <div className="border-b border-rule px-3 py-2.5">
          <input
            ref={inputRef}
            role="combobox"
            aria-expanded="true"
            aria-controls="palette-listbox"
            aria-activedescendant={active?.key}
            data-testid="palette-input"
            className={cx(inputCls, "border-0 px-1 py-1 text-[14px] focus:border-0")}
            placeholder="Jump to a task, run or project… answer, approve or steer"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onInputKeyDown}
          />
        </div>
        <ul id="palette-listbox" role="listbox" aria-label="Results" className="max-h-[60vh] overflow-y-auto p-2">
          {filtered.length === 0 && <li className="px-3 py-6 text-center text-[13px] italic text-ink-3">Nothing matches "{query}".</li>}
          {filtered.map((entry, i) => (
            <PaletteRow
              key={entry.key}
              entry={entry}
              active={i === activeIndex}
              expanded={expanded === entry.key}
              onHover={() => setActiveKey(entry.key)}
              onActivate={() => {
                setActiveKey(entry.key);
                if (entry.kind === "switch") switchTo(entry.project.id);
                else if (entry.kind === "project") go(`/projects/${entry.project.id}`);
                else if (entry.kind === "task") go(`/tasks/${entry.task.id}`);
                else if (entry.kind === "run") go(`/runs/${entry.lane.runId}`);
                else if (entry.kind === "steer-flag") go(`/tasks/${entry.item.taskId}`);
                else if (entry.kind === "actor") go(`/team/${entry.actor.id}`);
                else if (entry.kind === "page") go(entry.path);
                else setExpanded(expanded === entry.key ? null : entry.key);
              }}
              onSteer={() => {
                setActiveKey(entry.key);
                setExpanded(expanded === `steer-${entry.key}` ? null : `steer-${entry.key}`);
              }}
              steerExpanded={expanded === `steer-${entry.key}`}
              onDone={onClose}
            />
          ))}
        </ul>
        <p className="border-t border-rule px-3 py-1.5 font-mono text-[10.5px] text-ink-3">
          {expanded ? "tab move within this panel · esc back" : "↑↓ move · ↵ select · esc close"}
        </p>
      </div>
    </div>
  );
}

function PaletteRow({
  entry,
  active,
  expanded,
  steerExpanded,
  onHover,
  onActivate,
  onSteer,
  onDone,
}: {
  entry: Entry;
  active: boolean;
  expanded: boolean;
  steerExpanded: boolean;
  onHover: () => void;
  onActivate: () => void;
  onSteer: () => void;
  onDone: () => void;
}) {
  const rowCls = cx("rounded-md px-2.5 py-2 text-[13px]", active ? "bg-paper-2" : "");
  // The row itself is the option: per the ARIA combobox/listbox pattern, options are selected
  // virtually via aria-activedescendant on the input, not by being individually tabbable, so
  // there's no nested <button> here — just a click handler for the mouse. tabIndex={-1} on the
  // inline "Steer" affordance keeps it out of Tab order too (reached via Ctrl/Cmd+Enter instead);
  // it still works for a mouse click, with stopPropagation so it doesn't also trigger onActivate.
  return (
    <li
      id={entry.key}
      role="option"
      aria-selected={active}
      className={cx(rowCls, "cursor-pointer")}
      onMouseEnter={onHover}
      onClick={onActivate}
      data-testid="palette-option"
    >
      <div className="flex items-center gap-2">
        <div className="min-w-0 flex-1 truncate text-left">
          <RowLabel entry={entry} />
        </div>
        {entry.kind === "run" && (
          <Button
            variant="ghost"
            tabIndex={-1}
            className="px-2 py-0.5 text-[11px]"
            onClick={(e) => {
              e.stopPropagation();
              onSteer();
            }}
            data-testid="palette-steer-button"
          >
            Steer
          </Button>
        )}
      </div>
      {/* The row's onClick (onActivate) lives on this <li>; without stopping propagation here,
          every click inside the expanded panel — an option, the textarea, "Send" — would bubble
          up and re-toggle or navigate via onActivate on top of whatever the panel just did. */}
      {expanded && entry.kind === "decision" && (
        <div onClick={(e) => e.stopPropagation()}>
          <DecisionForm decision={entry.decision} onDone={onDone} />
        </div>
      )}
      {steerExpanded && entry.kind === "run" && (
        <div onClick={(e) => e.stopPropagation()}>
          <SteerForm runId={entry.lane.runId} onDone={onDone} />
        </div>
      )}
    </li>
  );
}

function RowLabel({ entry }: { entry: Entry }) {
  switch (entry.kind) {
    case "decision":
      return (
        <span className="flex items-center gap-2">
          <span className="rounded-sm bg-signal-soft px-1.5 py-px font-mono text-[10px] font-semibold uppercase tracking-wider text-ink">{VERB_LABEL[entry.verb]}</span>
          <TierChip tier={entry.decision.tier} />
          <span className="truncate">{entry.decision.question}</span>
        </span>
      );
    case "run":
      return (
        <span className="flex items-center gap-2">
          <span className="font-mono text-[10px] uppercase tracking-wider text-ink-3">run</span>
          <span className="font-medium">{entry.lane.name}</span>
          <span className="truncate text-ink-2">
            #{entry.lane.taskNumber} {entry.lane.taskTitle}
          </span>
        </span>
      );
    case "steer-flag":
      return (
        <span className="flex items-center gap-2">
          <span className="rounded-sm border border-high/40 bg-high/10 px-1 font-mono text-[10px] uppercase tracking-wider text-high">steer</span>
          <span className="truncate">
            #{entry.item.taskNumber} {entry.item.taskTitle} — {entry.item.message}
          </span>
        </span>
      );
    case "switch":
      return (
        <span className="flex items-center gap-2">
          <span className="font-mono text-[10px] uppercase tracking-wider text-ink-3">switch project</span>
          <span className="font-medium">{entry.project.name}</span>
        </span>
      );
    case "project":
      return (
        <span className="flex items-center gap-2">
          <span className="font-mono text-[10px] uppercase tracking-wider text-ink-3">project</span>
          <span>{entry.project.name}</span>
        </span>
      );
    case "task":
      return (
        <span className="flex items-center gap-2">
          <span className="font-mono text-[10px] uppercase tracking-wider text-ink-3">task</span>
          <span className="truncate">
            #{entry.task.number} {entry.task.title}
          </span>
        </span>
      );
    case "page":
      return (
        <span className="flex items-center gap-2">
          <span className="font-mono text-[10px] uppercase tracking-wider text-ink-3">page</span>
          <span>{entry.label}</span>
        </span>
      );
    case "actor":
      return (
        <span className="flex items-center gap-2">
          <span className="font-mono text-[10px] uppercase tracking-wider text-ink-3">{entry.actor.kind === "agent" ? "agent" : "person"}</span>
          <span>{entry.actor.name}</span>
        </span>
      );
  }
}

function DecisionForm({ decision, onDone }: { decision: DecisionDTO; onDone: () => void }) {
  const answer = useAnswer();
  const [choice, setChoice] = useState<string | null>(null);
  const [rationale, setRationale] = useState("");
  const textRef = useRef<HTMLTextAreaElement>(null);

  // mutateAsync + a plain await, not mutate(..., { onSuccess }): the instant this decision is
  // answered, the bridge refetch it triggers removes it from the filtered list and unmounts this
  // whole form, and a callback handed to TanStack Query isn't guaranteed to still run once the
  // component that called mutate() is gone. A bare `await` doesn't care — it resumes regardless.
  const submit = async (optionId: string) => {
    if (answer.isPending) return;
    if (NEEDS_RATIONALE.has(optionId) && !rationale.trim()) {
      setChoice(optionId);
      textRef.current?.focus();
      return;
    }
    // Swallow the rejection here — mutateAsync already recorded it on answer.error, which
    // <ErrorNote> renders below; letting it also surface as an unhandled promise rejection
    // (these call sites never await submit()) would just be console noise on top of that.
    try {
      await answer.mutateAsync({ id: decision.id, optionId, rationale });
      onDone();
    } catch {
      // handled via answer.error
    }
  };

  // Digit keys 1-9 answer directly, matching the <kbd> hints on each option (same shortcut as
  // Inbox.tsx's decision panel). Stop the row underneath from also seeing the keystroke.
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.target instanceof HTMLTextAreaElement) return;
    if (e.repeat || answer.isPending) return;
    const n = Number(e.key);
    const option = decision.options[n - 1];
    if (n >= 1 && option) {
      e.preventDefault();
      e.stopPropagation();
      void submit(option.id);
    }
  };

  return (
    <div className="mt-2 rounded-md border border-rule bg-paper px-3 py-2.5" data-testid="palette-decision-form" onKeyDown={onKeyDown}>
      <p className="font-mono text-[10.5px] uppercase tracking-wider text-ink-3">{KIND_LABEL[decision.kind]}</p>
      <ChiefNote note={decision.chiefNote} className="mt-1.5 !rounded-md !px-2.5 !py-2 !text-[12px]" />
      <div className="mt-1.5 flex flex-wrap gap-1.5">
        {decision.options.map((o, i) => (
          <button
            key={o.id}
            type="button"
            autoFocus={i === 0}
            data-option={o.id}
            onClick={() => void submit(o.id)}
            disabled={answer.isPending}
            className={cx(
              "rounded-sm border px-2 py-1 text-[12px] transition-colors disabled:opacity-50",
              decision.recommendation === o.id ? "border-signal bg-signal-soft/60" : "border-rule hover:border-ink",
            )}
          >
            <kbd className="mr-1 rounded border border-rule bg-card px-1 font-mono text-[10px] text-ink-3">{i + 1}</kbd>
            {o.label}
          </button>
        ))}
      </div>
      {choice && NEEDS_RATIONALE.has(choice) && (
        <div className="mt-2">
          <textarea
            ref={textRef}
            autoFocus
            rows={2}
            className={cx(inputCls, "text-[12px]")}
            placeholder={`Why "${choice.replace(/_/g, " ")}" — required`}
            value={rationale}
            onChange={(e) => setRationale(e.target.value)}
          />
          <Button variant="primary" className="mt-1.5 px-2.5 py-1 text-[11.5px]" onClick={() => void submit(choice)} disabled={!rationale.trim() || answer.isPending}>
            Send
          </Button>
        </div>
      )}
      <ErrorNote error={answer.error} />
    </div>
  );
}

function SteerForm({ runId, onDone }: { runId: string; onDone: () => void }) {
  const sendNote = useSendNote();
  const [text, setText] = useState("");
  const ref = useRef<HTMLTextAreaElement>(null);
  const runQuery = useRun(runId);
  const run = runQuery.data?.run;
  const blocked = run ? steerBlockedReason(run) : null;

  // Same reasoning as DecisionForm.submit: await mutateAsync directly rather than trust an
  // onSuccess callback to survive this form unmounting once the run list refetches, and swallow
  // the rejection here since sendNote.error (rendered via <ErrorNote>) already covers it.
  const submit = async () => {
    if (!text.trim() || sendNote.isPending) return;
    try {
      await sendNote.mutateAsync({ id: runId, text });
      onDone();
    } catch {
      // handled via sendNote.error
    }
  };

  // The form stays mounted while the run loads, or if loading fails: the hub still refuses a note it can't deliver.
  if (blocked)
    return (
      <p className="mt-2 rounded-md border border-rule bg-paper px-3 py-2.5 text-[12px] text-ink-2" data-testid="palette-steer-blocked">
        {blocked}
      </p>
    );

  return (
    <div className="mt-2 rounded-md border border-rule bg-paper px-3 py-2.5" data-testid="palette-steer-form">
      <label className="block">
        <span className="font-mono text-[10.5px] uppercase tracking-wider text-ink-3">Note to the running agent</span>
        <textarea
          ref={ref}
          autoFocus
          rows={2}
          className={cx(inputCls, "mt-1 text-[12px]")}
          placeholder="Use the existing date helper, not a new dependency…"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
              e.preventDefault();
              void submit();
            }
          }}
        />
      </label>
      <Button variant="primary" className="mt-1.5 px-2.5 py-1 text-[11.5px]" onClick={() => void submit()} disabled={!text.trim() || sendNote.isPending} data-testid="palette-steer-send">
        Send note
      </Button>
      <ErrorNote error={sendNote.error ?? runQuery.error} />
    </div>
  );
}
